import { ArrowLeftIcon } from '@heroicons/react/20/solid'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client'
import { GET_COUNTRY_LIST } from 'graphql/common/queries'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { GET_SHUTTLE_SERVICE_DETAILS } from 'graphql/shuttleService/queries'
import { ADD_SHUTTLE_SERVICE_PROVIDER, UPDATE_SHUTTLE_SERVICE_PROVIDER } from 'graphql/shuttleService/mutations'
import ShuttleServiceFormFields from '../components/ShuttleServiceFormFields'
import { useShuttleServiceForm, formatShuttleServiceData } from '../hooks/useShuttleServiceForm'

export default function ShuttleServiceEditForm() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [submitLoading, setSubmitLoading] = useState(false)
    const [shuttleInfo, setShuttleInfo] = useState(null)
    const [countries, setCountries] = useState([])

    const handleBack = () => navigate('/system-config/shuttle-services')
    const handleDialogClose = () => {
        setShowSucDialog(false)
        setShowErrDialog(false)
    }

    const [getCountryList] = useLazyQuery(GET_COUNTRY_LIST, {
        fetchPolicy: 'network-only',
        onCompleted: data => data.country && setCountries(data.country),
    })

    const [getShuttleDetails, { loading: shuttleLoading }] = useLazyQuery(GET_SHUTTLE_SERVICE_DETAILS, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.searchShuttleServiceProvider?.providers?.[0]) {
                setShuttleInfo(data.searchShuttleServiceProvider.providers[0])
            }
        },
    })

    useEffect(() => {
        getCountryList({ variables: { code: null } })
        if (id) {
            getShuttleDetails({
                variables: {
                    searchShuttleServiceProviderId: id,
                    page: { limit: 1, offset: 0 },
                },
            })
        }
    }, [id])

    const [addShuttleService] = useMutation(ADD_SHUTTLE_SERVICE_PROVIDER, {
        onCompleted: data => {
            formik.setSubmitting(false)
            setSubmitLoading(false)
            if (data?.addShuttleServiceProvider.code === '200' && data?.addShuttleServiceProvider.success) {
                setShowSucDialog(true)
                setTimeout(handleBack, 500)
            } else {
                setErrorMessage(data?.addShuttleServiceProvider.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            formik.setSubmitting(false)
            setErrorMessage(error.message)
            setShowErrDialog(true)
        },
    })

    const [updateShuttleService] = useMutation(UPDATE_SHUTTLE_SERVICE_PROVIDER, {
        onCompleted: data => {
            formik.setSubmitting(false)
            setSubmitLoading(false)
            if (data?.updateShuttleServiceProvider.code === '200' && data?.updateShuttleServiceProvider.success) {
                setShowSucDialog(true)
                setTimeout(handleBack, 500)
            } else {
                setErrorMessage(data?.updateShuttleServiceProvider.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            formik.setSubmitting(false)
            setErrorMessage(error.message)
            setShowErrDialog(true)
        },
    })

    const formik = useShuttleServiceForm(
        {
            provider_name: shuttleInfo?.provider_name || '',
            service_description_en: shuttleInfo?.service_description?.en || '',
            service_description_ar: shuttleInfo?.service_description?.ar || '',
            contact_person: shuttleInfo?.contact?.person || '',
            contact_mobile_number: shuttleInfo?.contact?.mobile_number || '',
            contact_email: shuttleInfo?.contact?.email || '',
            contact_address: shuttleInfo?.contact?.address || '',
            country_code: shuttleInfo?.country_code || '',
            price_base: shuttleInfo?.price?.base?.toString() || '',
            price_tax: shuttleInfo?.price?.tax?.toString() || '',
            price_currency: shuttleInfo?.price?.currency || 'BHD',
            isactive: shuttleInfo?.isactive ?? true,
        },
        values => {
            setSubmitLoading(true)
            const formattedData = formatShuttleServiceData(values)
            if (id) {
                updateShuttleService({
                    variables: {
                        provider: {
                            ...formattedData.provider,
                            provider_id: id,
                        },
                    },
                })
            } else {
                addShuttleService({ variables: formattedData })
            }
        }
    )

    if (shuttleLoading) {
        return (
            <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary"></div>
                <p className="pt-3 text-primary font-semibold">Loading shuttle service details...</p>
            </div>
        )
    }

    return (
        <>
            <div className="mb-8 flex justify-between items-center">
                <button
                    type="button"
                    onClick={handleBack}
                    className="inline-flex items-center gap-x-0.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Back')}
                </button>
                <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={handleBack}
                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                        {t('Cancel')}
                    </button>
                    <button
                        disabled={formik.isSubmitting}
                        onClick={formik.handleSubmit}
                        type="button"
                        className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    >
                        {t('Save')}
                    </button>
                </div>
            </div>
            <form>
                <div className="space-y-6">
                    <ShuttleServiceFormFields formik={formik} countries={countries} />
                    <div className="pt-4 mt-2 border-t border-gray-200 flex justify-end">
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={handleBack}
                                className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                                {t('Cancel')}
                            </button>
                            <button
                                disabled={formik.isSubmitting}
                                onClick={formik.handleSubmit}
                                type="button"
                                className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                            >
                                {t('Save')}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={errorMessage} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog
                    status={true}
                    title={t('Success')}
                    message={t('Shuttle service updated successfully')}
                    onClose={handleDialogClose}
                />
            )}
            {submitLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                </div>
            )}
        </>
    )
}
